import React from 'react';
import styled from 'styled-components';
import HBText, { ITextProps } from '../HBText/HBText';
import { vars } from '../../theme';
import HBModal, { IModal } from '../HBModal/HBModal';
import { Body as ModalBody } from '../HBModal/HBModal.styles';
import HBButton from '../HBButton/HBButton';
import { useWindowSize } from '../../hooks/useWindowSize';
import { mediumUp } from '../../styles/breakpoints';
import HBMediaElement, {
    IMediaElementProps
} from '../HBMediaElement/HBMediaElement';

export interface IEducationalModalProps
    extends Pick<IModal, 'isOpen' | 'onClose'> {
    /** Cloudinary or URL path for the media element displayed at the top of the modal. */
    mediaPath: IMediaElementProps['path'];
    /** Heading text displayed below the media image. */
    title: ITextProps['children'];
    /** Primary descriptive text displayed below the title. */
    body: ITextProps['children'];
    /** Optional secondary text rendered below the body in a smaller, muted style. */
    learnMore?: ITextProps['children'];
    /** Callback fired when the primary action button is clicked. */
    onButtonClick: () => void;
    /** Label for the primary action button at the bottom of the modal. */
    buttonText: ITextProps['children'];
}

const HBEducationalModal = ({
    mediaPath,
    title,
    body,
    learnMore,
    onButtonClick,
    buttonText,
    ...props
}: IEducationalModalProps) => {
    const { isMediumUp } = useWindowSize();

    return (
        <Modal {...props} hideBackdrop={isMediumUp}>
            <HBMediaElement path={mediaPath} width={160} />
            <Title type="title20">{title}</Title>
            <Body type="body16">{body}</Body>
            {learnMore && (
                <LearnMore type="body14" color="text.secondary">
                    {learnMore}
                </LearnMore>
            )}
            <Button size="md" onClick={onButtonClick} testId="accept--button">
                {buttonText}
            </Button>
        </Modal>
    );
};

export default HBEducationalModal;

// -------------------------- Styles -------------------------- //

const Modal = styled(HBModal)`
    width: 386px;
    margin-inline: 20px;
    ${mediumUp} {
        margin-inline: 0;
        position: fixed;
        bottom: 40px;
        right: 20px;
        box-shadow: ${vars.shadow['50']};
    }
    ${ModalBody} {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 52px 20px 20px;
        ${mediumUp} {
            padding: 42px 32px 32px;
        }
    }
`;

const Title = styled(HBText)`
    margin-top: 40px;
`;

const Body = styled(HBText)`
    margin-top: 8px;
`;

const LearnMore = styled(HBText)`
    margin-top: 20px;
`;

const Button = styled(HBButton)`
    margin-top: 20px;
    align-self: stretch;
    ${mediumUp} {
        margin-top: 32px;
        align-self: center;
    }
`;
